import { ErrorResponseInterface } from '@systemeio/util'
import { AxiosInstance, AxiosRequestConfig } from 'axios'
import { useRef } from 'react'
import { useFetch } from 'shared/hooks/use-fetch'
import { RequestMethodsEnum, UseFetchProps, methodsWithoutBody } from 'shared/hooks/use-fetch/types'

export const useFetcherWithAbort = <
  M extends RequestMethodsEnum,
  R = void,
  D = any,
  BadRequestError = ErrorResponseInterface,
>(
  args: UseFetchProps<R, M, BadRequestError>,
) => {
  const abortController = useRef<Map<string, AbortController>>(new Map())
  const cancelRequest = (key: string = 'default') => {
    abortController.current.get(key)?.abort()
    abortController.current.delete(key)
  }

  const { fetcher, isFetching } = useFetch<M, R, D, BadRequestError>(args)
  const abortableFetcher = (
    firstArg: Parameters<AxiosInstance[M]>[0],
    secondArg?: Parameters<AxiosInstance[M]>[1],
    thirdArg?: Parameters<AxiosInstance[M]>[2],
    key: string = 'default',
  ) => {
    cancelRequest(key)

    abortController.current.set(key, new AbortController())

    const withoutBody = methodsWithoutBody.includes(args.method)

    const signal = abortController.current.get(key)?.signal

    let typedSecondArg = secondArg as (D & AxiosRequestConfig<D>) | undefined
    let typedThirdArg = thirdArg as AxiosRequestConfig<D> | undefined

    if (signal) {
      if (withoutBody) {
        typedSecondArg = (
          typedSecondArg
            ? {
                ...typedSecondArg,
                signal,
              }
            : { signal }
        ) as AxiosRequestConfig<D> & D
      } else {
        typedThirdArg = typedThirdArg
          ? {
              ...typedThirdArg,
              signal,
            }
          : { signal }
      }
    }

    const injectedArgs = [firstArg, typedSecondArg, typedThirdArg] as unknown as Parameters<
      AxiosInstance[M]
    >
    return fetcher(...injectedArgs)
  }

  return { fetcher: abortableFetcher, isFetching, cancelRequest }
}
